import React, { useEffect, useState, useCallback, useRef } from 'react';
import './BackToTop.css';

/**
 * BackToTop
 * - Appears after the user scrolls beyond `threshold` (in px)
 * - Respects prefers-reduced-motion
 * - Hidden on desktop by default; pass `showOnDesktop` to show it there too
 */
export default function BackToTop({ threshold = 280, showOnDesktop = false }) {
  const [show, setShow] = useState(false);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      // Use rAF to throttle scroll updates
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          setShow(window.scrollY > threshold); // show when scrolled past threshold
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    onScroll(); // initial check
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  const scrollToTop = useCallback(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    try {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    } catch {
      // Fallback for very old browsers
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <button
      type="button"                                       // avoid form submit
      className={`back-to-top ${show ? 'show' : ''} ${showOnDesktop ? 'show-desktop' : ''}`}
      aria-label="Back to top"
      title="Back to top"
      onClick={scrollToTop}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && scrollToTop()}
    >
      {/* Up arrow SVG (no external icon libs) */}
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 5l-7 7h4v7h6v-7h4z" fill="currentColor" />
      </svg>
    </button>
  );
}
